import type { CartItem, Cart } from '~/utils/easyApi'

export function useGpoCompliance() {
  const userStore = useUserStore()
  const { getItemPrice } = usePriceUtils()

  const userClinicChainHandle = computed(() => userStore.gpoHandle)
  const showGpoComplianceInfo = computed(() => unref(userStore.gpoHandle) != 'none')
  const userClinicChainName = computed(() => userStore.data?.clinicChain?.name)

  function isItemGpoCompliant(item: CartItem): boolean {
    if (!unref(userClinicChainHandle) || !item.clinicChains?.length) {
      return false
    }
    return item.clinicChains.some((chain: any) => chain.handle === unref(userClinicChainHandle))
  }

  function calculateGpoCompliance(cartItems: CartItem[]): {
    totalGmv: number
    gpoGmv: number
    compliancePercentage: number
    complianceLevel: 'low' | 'medium' | 'high' | 'very-high' | 'excellent'
    emoji: string
  } {
    const totalGmv = cartItems.reduce((sum, item) =>
      sum + (getItemPrice(item) * item.quantity), 0)

    const gpoGmv = cartItems
      .filter(item => isItemGpoCompliant(item))
      .reduce((sum, item) =>
        sum + (getItemPrice(item) * item.quantity), 0)

    const compliancePercentage = totalGmv > 0 ? (gpoGmv / totalGmv) * 100 : 0

    let complianceLevel: 'low' | 'medium' | 'high' | 'very-high' | 'excellent'
    let emoji: string

    if (compliancePercentage < 30) {
      complianceLevel = 'low'
      emoji = '☹️'
    }
    else if (compliancePercentage < 50) {
      complianceLevel = 'medium'
      emoji = '😐'
    }
    else if (compliancePercentage < 70) {
      complianceLevel = 'high'
      emoji = '😊'
    }
    else if (compliancePercentage < 90) {
      complianceLevel = 'very-high'
      emoji = '🤩'
    }
    else {
      complianceLevel = 'excellent'
      emoji = '🥳'
    }

    return {
      totalGmv,
      gpoGmv,
      compliancePercentage: Math.round(compliancePercentage),
      complianceLevel,
      emoji,
    }
  }

  function calculateOverallCompliance(carts: Cart[]): {
    totalGmv: number
    gpoGmv: number
    compliancePercentage: number
    complianceLevel: 'low' | 'medium' | 'high' | 'very-high' | 'excellent'
    emoji: string
  } {
    const allItems = carts.flatMap(cart => cart.items)
    return calculateGpoCompliance(allItems)
  }

  return {
    calculateGpoCompliance,
    calculateOverallCompliance,
    isItemGpoCompliant,
    userClinicChainHandle,
    userClinicChainName,
    showGpoComplianceInfo,
  }
}
